import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { useTelemetry } from '../telemetry/useTelemetry'
import { NAV_SURFACES } from './migration'
import { SurfaceIcon } from './SurfaceIcon'

/**
 * Mobile nav (AE1): re-skinned classic bottom nav + "Meer" sheet — no
 * workspace below the desktop boundary. Pins: hub / projects / mail /
 * hermes (mail replaces V1's inbox pin since Mail is the merged
 * surface — IA validated at the U9 gate).
 */
const PINNED = ['home', 'hoxton', 'tovos', 'mail']

export function BottomNavV2() {
  const { pathname } = useLocation()
  const [sheetOpen, setSheetOpen] = useState(false)
  const { inboxUnread, mailUnread } = useTelemetry()

  useEffect(() => {
    setSheetOpen(false)
  }, [pathname])

  // Longest matching navTo wins, so a nested entry (/office/pdfcn) doesn't
  // also light its parent (/office) — mirrors migration.ts surfaceForPath.
  const activeTo =
    pathname === '/'
      ? '/'
      : NAV_SURFACES.filter((s) => s.navTo !== '/' && pathname.startsWith(s.navTo)).sort(
          (a, b) => b.navTo.length - a.navTo.length,
        )[0]?.navTo
  const isActive = (to: string) => to === activeTo
  const pinned = PINNED.map((id) => NAV_SURFACES.find((s) => s.id === id)).filter(
    (s): s is NonNullable<typeof s> => Boolean(s),
  )
  const rest = NAV_SURFACES.filter((s) => !PINNED.includes(s.id))

  return (
    <>
      <nav className="v2-bottom-nav" aria-label="Primary navigation (mobile)">
        {pinned.map((s) => {
          const badge =
            s.id === 'mail' && mailUnread ? mailUnread : s.id === 'inbox' && inboxUnread ? inboxUnread : undefined
          return (
            <Link
              key={s.id}
              to={s.navTo}
              className={cn(isActive(s.navTo) && 'active', s.status !== 'live' && 'dim')}
              aria-current={isActive(s.navTo) ? 'page' : undefined}
            >
              <SurfaceIcon id={s.id} />
              <span>{s.label}</span>
              {badge ? <span className="v2-bnav-badge">{badge > 99 ? '99+' : badge}</span> : null}
            </Link>
          )
        })}
        <button
          type="button"
          className={cn(sheetOpen && 'active')}
          onClick={() => setSheetOpen((v) => !v)}
          aria-expanded={sheetOpen}
          aria-controls="v2-meer-sheet"
        >
          <SurfaceIcon id="more" />
          <span>Meer</span>
        </button>
      </nav>

      {sheetOpen && (
        <div className="v2-sheet-backdrop" onClick={() => setSheetOpen(false)}>
          <div
            id="v2-meer-sheet"
            className="v2-sheet"
            role="dialog"
            aria-label="Meer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="v2-sheet-handle" aria-hidden="true" />
            <h3 className="v2-sheet-title">Meer</h3>
            <div className="v2-sheet-grid">
              {rest.map((s) => s.external ? (
                <a key={s.id} href={s.navTo} className="v2-sheet-item">
                  <SurfaceIcon id={s.id} />
                  <span>{s.label}</span>
                </a>
              ) : (
                <Link
                  key={s.id}
                  to={s.navTo}
                  className={cn('v2-sheet-item', isActive(s.navTo) && 'active', s.status !== 'live' && 'dim')}
                  aria-current={isActive(s.navTo) ? 'page' : undefined}
                  onClick={() => setSheetOpen(false)}
                >
                  <SurfaceIcon id={s.id} />
                  <span>{s.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
